import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Animated,
  PanResponder,
  Platform,
} from 'react-native';
import { useNavigation, useTheme } from '@react-navigation/native';
import { Theme } from '@theme';
import { Icon as CustomIcon, Header, SearchBar, GroupAvatar, FeatureTooltip } from '@components';
import { getCommonStyles, Scope, SelectionItem, groupByMoment } from '@utils';
import dayjs from 'dayjs';
import DefaultAvatar from './components/DefaultAvatar';
import { useDeleteScheduledMessage, useLoadAllPages, usePaginateScheduledMessages, useScreen } from '@hooks';
import { useCreationStackIntegration, ScheduledMessage } from '@hooks';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MomentFilter, getMomentFilterMap } from '../components/MomentFilter';
import { useLanguage } from '@context';

const ScheduledMessages: React.FC = () => {
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const {isWeb, isMobile, isDesktop} = useScreen();
  const { t } = useLanguage();
  const commonStyles = getCommonStyles(theme);
  const styles = getStyles(theme);
  const [searchText, setSearchText] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<Scope>(Scope.PRIVATE);
  const [swipedMessageId, setSwipedMessageId] = useState<string | null>(null);
  const { showScheduledMessageCreation, isCreationStackEnabled } = useCreationStackIntegration();
  
  const momentFilterMap = getMomentFilterMap(t)
  const [momentFilter, setMomentFilter] = useState<SelectionItem>({id: 'default', name: t('common.filter')})
  
  const momentParam = momentFilter.id !== 'default' ? momentFilter.id as 'today' | 'week' | 'month' | 'later' : undefined;
  const scheduledQuery = usePaginateScheduledMessages({ 
    search: searchText || undefined, 
    scope: selectedFilter,
    moment: momentParam
  });
  const { flatData, totalCount, isFetched, isSuccess, refetch } = scheduledQuery;
  // The list is grouped by moment on the device: load every page, not the first only.
  useLoadAllPages(scheduledQuery);
  // Soonest first, whatever the order the server answers in.
  const scheduledMessages = [...flatData].sort(
    (a, b) => dayjs(a.scheduledDate).valueOf() - dayjs(b.scheduledDate).valueOf(),
  );
  // A failed load must not look like an empty list. When a list is already
  // on screen and a refresh fails, the list stays. Not `isError`: it goes back
  // to false while a new attempt is running, and the page would show
  // « 0 résultat » during the whole retry.
  const loadFailed = isFetched && !isSuccess && scheduledMessages.length === 0;
  const { mutate: deleteScheduledMessage } = useDeleteScheduledMessage();

  // Group by moment when no specific moment filter is selected
  const groupedByMoment = groupByMoment(scheduledMessages, 'scheduledDate')

  const handleCreateMessage = () => {
    if(isCreationStackEnabled) {
      showScheduledMessageCreation({
        scope: selectedFilter
      });
    } else {
      navigation.navigate('CreateScheduledMessage', {
        scope: selectedFilter
      });
    }
  };

  const handleEditMessage = (messageId: string) => {
    const message = scheduledMessages.find(m => m.id === messageId);
    if (message) {
      if (isCreationStackEnabled) {
        showScheduledMessageCreation({
          scheduledMessageId: messageId,
          isEdit: true
        }, 1);
      } else {
        navigation.navigate('ComposeMessage', { 
          scheduledMessageId: messageId,
          isEdit: true
        });
      }
    }
  };

  const handleDeleteMessage = (messageId: string) => {
    deleteScheduledMessage({ id: messageId });
    setSwipedMessageId(null);
  };

  const goToComposeMessage = (message: ScheduledMessage) => {
    if (isCreationStackEnabled) {
      showScheduledMessageCreation({
        scheduledMessageId: message.id,
        isEdit: false
      }, 1);
    } else {
      navigation.navigate('ComposeMessage', { 
        scheduledMessageId: message.id,
        isEdit: false
      });
    }
  };

  const createPanResponder = (messageId: string) => {
    const translateX = new Animated.Value(0);
    
    return PanResponder.create({
      onMoveShouldSetPanResponder: (evt, gestureState) => {
        return Math.abs(gestureState.dx) > 10;
      },
      onPanResponderMove: (evt, gestureState) => {
        if (gestureState.dx < 0) {
          translateX.setValue(Math.max(gestureState.dx, -60));
        }
      },
      onPanResponderRelease: (evt, gestureState) => {
        if (gestureState.dx < -30) {
          Animated.spring(translateX, {
            toValue: -60,
            useNativeDriver: true,
          }).start();
          setSwipedMessageId(messageId);
        } else {
          Animated.spring(translateX, {
            toValue: 0,
            useNativeDriver: true,
          }).start();
          setSwipedMessageId(null);
        }
      },
    });
  };

  const renderMessageItem = (message: ScheduledMessage) => {
    const panResponder = createPanResponder(message.id);
    const translateX = new Animated.Value(swipedMessageId === message.id ? -45 : 0);

    // Swipe-to-delete is a native touch gesture. On web the PanResponder's
    // responder handlers sit between the row's outer TouchableOpacity and the
    // inner edit pencil TouchableOpacity, and they intercept the tap so the
    // pencil's onPress never fires (RN-Web nested-touchable / responder
    // interception — see CLAUDE.md). Web deletes via the detail panel's trash
    // button, so the swipe handlers aren't needed there; dropping them lets the
    // pencil behave like the working action icons in ContactsList.
    const panHandlers =
      Platform.OS === 'web' ? {} : panResponder.panHandlers;

    return (
      <TouchableOpacity key={message.id} onPress={() => goToComposeMessage(message)}>
        <Animated.View
          style={[
            styles.messageItem,
            { transform: [{ translateX }] }
          ]}
          {...panHandlers}
        >
          <Text style={styles.messageDateTime}>{dayjs(message.scheduledDate).format('DD/MM/YYYY HH:mm')}</Text>
          <View style={styles.messageContainer}>
              {message.scope === Scope.GROUP && message.groupParticipants?.length ? (
                <GroupAvatar profiles={message.groupParticipants.map(p => ({ id: p.id, avatarUrl: p.avatarUrl ?? undefined }))} />
              ) : (
                <View style={styles.avatarContainer}>
                  {message.recipient.photo ? (
                    <Image source={{ uri: message.recipient.photo}} style={styles.avatar} />
                  ) : (
                    <DefaultAvatar name={message.recipient.name} scope={message.scope as Scope.PRIVATE | Scope.PROFESSIONAL | Scope.GROUP | Scope.CATEGORY | Scope.HASHTAG} />
                  )}
                </View>
              )}
            <View style={styles.messageContent}>
              <Text style={styles.contactName}>{message.recipient.name}</Text>
              <Text style={styles.messageText} numberOfLines={2}>
                {message.message}
              </Text>
            </View>
            <View style={styles.messageActions}>
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => handleEditMessage(message.id)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityLabel={t('common.edit')}
              >
                <CustomIcon name="editPen" size={16} color={theme.colors.greyBlue} />
              </TouchableOpacity>
            </View>
          </View>
        </Animated.View>
        
        {swipedMessageId === message.id && (
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => handleDeleteMessage(message.id)}
          >
            <CustomIcon name="trash" size={24} color="white" />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  };

  const renderSection = (key: string, items: ScheduledMessage[]) => {
    if (!isDesktop && items.length === 0) return null;
    
    return (
      <>
        <View style={styles.dateSection}>
          <View style={styles.dateLine} />
            <Text style={styles.dateText}>{momentFilterMap[key]}</Text>
          <View style={styles.dateLine} />
        </View>
        {items.map(renderMessageItem)}
      </>
    );
  };

  const tabs = [
    { label: t('scheduledMessagesScreen.private'), key: Scope.PRIVATE },
    { label: t('scheduledMessagesScreen.pro'), key: Scope.PROFESSIONAL },
    { label: t('scheduledMessagesScreen.groups'), key: Scope.GROUP },
    //{ label: t('scheduledMessagesScreen.categoriesTab'), key: Scope.CATEGORY },
  ];

  return (
    <SafeAreaView style={styles.container}>
      <Header
        title={t('scheduledMessagesScreen.title')}
        iconLeft={isWeb? undefined : "arrowBack"}
        iconRight="plus"
        onPressRight={handleCreateMessage}
      />

      <FeatureTooltip
        id="scheduled_messages"
        title={t('featureTooltip.scheduled_messages.title')}
        description={t('featureTooltip.scheduled_messages.description')}
        icon="calendarStar"
      />

      <View style={isDesktop? styles.searchContainerWeb : styles.searchContainerMobile}>
        <View style={[styles.searchContainer, !isMobile && commonStyles.searchContainerMaxWidth]}>
          <SearchBar value={searchText} onChangeText={setSearchText} />
        </View>
        {/* Tabs dans un ScrollView horizontal */}
        <View style={styles.tabContainer}>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabScrollContent}
          >
            {tabs.map((tab, index) => {
              const filterKey = tab.key;
              const isActive = selectedFilter === filterKey;
              
              // Couleurs spécifiques pour chaque onglet selon la maquette
              const getActiveColor = () => {
                switch (filterKey) {
                  case Scope.PRIVATE: return theme.colors.green;  // Vert pour Privé
                  case Scope.PROFESSIONAL: return theme.colors.primary;    // Bleu pour Pro
                  case Scope.GROUP: return theme.colors.purple;  // Violet pour Groupes
                  //case Scope.CATEGORY: return theme.colors.orange; // Orange pour Catégories
                  default: return theme.colors.primary;
                }
              };
              
              const activeColor = getActiveColor();
              
              return (
                <TouchableOpacity
                  key={tab.key}
                  style={[
                    styles.tab,
                    isActive && { ...styles.activeTab, borderColor: activeColor },
                  ]}
                  onPress={() => setSelectedFilter(filterKey)}
                >
                  {index === 0 && <CustomIcon name="lock" size={16} color={isActive ? activeColor : theme.colors.greyBlue} style={styles.tabIcon} />}
                  {index === 1 && <CustomIcon name="work" size={16} color={isActive ? activeColor : theme.colors.greyBlue} style={styles.tabIcon} />}
                  {index === 2 && <CustomIcon name="people" size={16} color={isActive ? activeColor : theme.colors.greyBlue} style={styles.tabIcon} />}
                  {/*{index === 3 && <CustomIcon name="category" size={16} color={isActive ? activeColor : theme.colors.greyBlue} style={styles.tabIcon} />}*/}
                  <Text style={[styles.tabText, isActive && { ...styles.activeTabText, color: activeColor }]}>
                    {tab.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
            {/*<TouchableOpacity 
              style={[
                styles.hashtagTab,
                selectedFilter === Scope.HASHTAG && styles.activeHashtagTab
              ]}
              onPress={() => setSelectedFilter(Scope.HASHTAG)}
            >
              <Text style={[
                styles.hashtagText,
                selectedFilter === Scope.HASHTAG && styles.activeHashtagText
              ]}>#</Text>
            </TouchableOpacity>*/}
            <MomentFilter filter={momentFilter} setFilter={setMomentFilter} />
          </ScrollView>
        </View>
      </View>

      {!loadFailed && (
        <Text style={styles.resultsCount}>{t('common.resultsCount', { count: totalCount })}</Text>
      )}
      {loadFailed ? (
        <View style={styles.emptyStateContainer}>
          <Text style={styles.loadErrorText}>{t('scheduledMessagesScreen.loadError')}</Text>
          <TouchableOpacity
            style={styles.createMessageButton}
            onPress={() => refetch()}
            testID="scheduled-messages-retry"
          >
            <Text style={styles.createButtonText}>{t('common.retry')}</Text>
          </TouchableOpacity>
        </View>
      ) : isFetched && scheduledMessages.length === 0 ? (
        <View style={styles.emptyStateContainer}>
          <TouchableOpacity style={styles.createMessageButton} onPress={handleCreateMessage}>
            <CustomIcon name="plus" size={14} color={theme.colors.primary} style={styles.createButtonIcon} />
            <Text style={styles.createButtonText}>{t('scheduledMessagesScreen.createMessage')}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView style={styles.messagesList} showsVerticalScrollIndicator={false}>
          {momentFilter.id !== 'default' && renderSection(momentFilter.id, scheduledMessages)}
          {momentFilter.id === 'default' && <View style={isDesktop ? styles.contentListWeb : styles.contentList}>
            <View style={isDesktop && styles.content}>
              {renderSection('today', groupedByMoment.today)}
              {renderSection('week', groupedByMoment.week)}
            </View>
            {isDesktop && <View style={styles.vSpacer} />}
            <View style={isDesktop && styles.content}>
              {renderSection('month', groupedByMoment.month)}
              {renderSection('later', groupedByMoment.later)}
            </View>
          </View>}
        </ScrollView>
      )}
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  searchContainerMobile: {
    marginBottom: theme.spacing.md,
  },
  searchContainerWeb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.lg,
  },
  searchContainer: {
    paddingHorizontal: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
  },
  // Tabs comme dans Notes
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    alignItems: 'center',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs * 2,
    marginRight: theme.spacing.sm,
    borderRadius: 20,
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.lightGrey,
  },
  activeTab: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.primary,
  },
  tabIcon: {
    marginRight: theme.spacing.xs,
  },
  tabText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.greyBlue,
    fontWeight: '500',
  },
  activeTabText: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  hashtagTab: {
    padding: theme.spacing.xs,
    marginRight: theme.spacing.sm,
    borderRadius: 20,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.lightGrey,
  },
  hashtagText: {
    fontSize: theme.fontSize.small,
    fontWeight: '600',
    color: theme.colors.text,
  },
  activeHashtagTab: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.primary,
  },
  activeHashtagText: {
    color: theme.colors.primary,
  },
  contentList: {
    flex: 1,
  },
  contentListWeb: {
    flex: 1,
    flexDirection: 'row', 
    gap: theme.spacing.lg,
  },
  content: {
    flex: 1,
  },
  vSpacer: {
    height: '100%',
    width: 1,
    backgroundColor: theme.colors.lightGrey,
  },
  // Divider Aujourd'hui comme dans Notes
  dateSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.background,
  },
  dateLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.lightGrey,
  },
  dateText: {
    paddingHorizontal: theme.spacing.md,
    fontSize: theme.fontSize.small,
    color: theme.colors.secondary,
  },
  resultsCount: {
    fontSize: theme.fontSize.extraSmall,
    color: theme.colors.greyBlue,
    paddingHorizontal: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
  },
  messagesList: {
    flex: 1,
  },
  // Style de contact comme dans Notes
  messageItem: {
    marginHorizontal: theme.spacing.xxl,
    paddingVertical: theme.spacing.md,
  },
  avatarContainer: {
    width: 50,
    height: 50,
    overflow: 'hidden',
    marginRight: 12,
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  messageContent: {
    flex: 1,
  },
  messageActions: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 8,
  },
  contactName: {
    fontSize: theme.fontSize.medium,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 2,
  },
  editButton: {
    padding: 2,
  },
  messageText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.greyBlue,
    lineHeight: 18,
  },
  messageDateTime: {
    fontSize: theme.fontSize.extraSmall,
    color: theme.colors.primary,
    fontWeight: '400',
    marginBottom: theme.spacing.md
  },
  messageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 60,
    backgroundColor: '#FF3B30',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: 8,
  },
  tabScrollContent: {
    paddingHorizontal: theme.spacing.sm,
  },
  // Styles pour l'état vide et le bouton centré
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: theme.spacing.xxxl * 2,
    backgroundColor: theme.colors.white,
  },
  createMessageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.white,
  },
  createButtonIcon: {
    marginRight: theme.spacing.sm,
  },
  createButtonText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.primary,
    fontWeight: '500',
  },
  loadErrorText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.greyBlue,
    textAlign: 'center',
    marginBottom: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
  },
});

export default ScheduledMessages; 